import { cn } from "@/lib/utils";
import InlineButton from "@/components/ui/inline-button";
import Link from "next/link";
import { StaffingIcon } from "@/components/icons";
import {
  LinkBox,
  LinkOverlay,
} from "@/components/ui/link-overlay/link-overlay";

interface CardLink {
  name: string;
  href: string;
  prefetch?: boolean;
}

export interface IconTextCardListedProps {
  className?: string;
  icon?: React.ReactNode;
  iconClassName?: string;
  title?: string;
  titleClassName?: string;
  items?: CardLink[];
  itemsGroupClassName?: string;
  itemsClassName?: string;
  cta?: {
    href: string;
    text: string;
    prefetch?: boolean;
    ariaLabel?: string;
    className?: string;
    color?: "orange" | "teal";
  };
  linkBox?: boolean;
}

export const IconTextCardListed: React.FC<IconTextCardListedProps> = ({
  className = "",
  icon,
  iconClassName = "",
  title = "Icon Title",
  titleClassName = "",
  items = [],
  itemsGroupClassName = "",
  itemsClassName = "",
  cta,
  linkBox = false,
}) => {
  const Comp = linkBox ? LinkBox : "div";
  return (
    <Comp
      className={cn(
        "flex flex-col items-center overflow-hidden rounded-[20px] bg-white p-8 hover:bg-primary-100",
        "shadow-[4px_8px_24px_2px_rgba(82,77,75,0.1)]",
        "transition ease-out",
        className
      )}
    >
      <div className={cn("mx-auto", iconClassName)}>
        {icon ? icon : <StaffingIcon />}
      </div>
      <h3
        className={cn(
          "lg:subheading-3-500 mx-auto mt-5 text-center font-montserrat text-subheading-3-500 text-secondary-500",
          titleClassName
        )}
      >
        {cta && linkBox ? (
          <Link
            href={cta.href}
            aria-label={cta.ariaLabel}
            prefetch={cta.prefetch}
          >
            {title}
          </Link>
        ) : (
          title
        )}
      </h3>
      {items.length > 0 && (
        <ul
          className={cn(
            "mx-auto mt-4 flex flex-col items-center text-center font-montserrat",
            itemsGroupClassName
          )}
        >
          {items.map((item, index) => (
            <li
              key={`${item.name}-${index}`}
              className={cn("mx-auto flex w-fit", itemsClassName)}
            >
              <Link
                className="font-roboto text-body-1-300 text-neutral-500 hover:!text-body-1-400 hover:text-primary-500"
                href={item.href}
                prefetch={item.prefetch}
              >
                <span className="flex p-2">{item.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {cta && !linkBox && (
        <InlineButton
          className={cn("mt-4 lg:mt-auto", cta.className)}
          variant="default"
          size="sm"
          color={cta.color ?? "teal"}
          asChild
        >
          <Link
            href={cta.href}
            aria-label={cta.ariaLabel}
            prefetch={cta.prefetch}
          >
            {cta.text}
          </Link>
        </InlineButton>
      )}
      {cta && linkBox && <LinkOverlay href={cta.href} />}
    </Comp>
  );
};

export default IconTextCardListed;
